import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Bus, LayoutDashboard, Users, Car, ClipboardList, ShieldAlert, LogOut, Radio, CreditCard, BarChart3, Map, Link as LinkIcon, MapPin, Route, Award, Sparkles, Bluetooth, Settings as SettingsIcon, UserCog, RefreshCw, CalendarDays, Wifi, WifiOff, FileSpreadsheet, Activity } from "lucide-react";
import { Toaster } from "sonner";
import { realtime } from "@/lib/realtime";
import HelpButton from "@/components/HelpButton";

function RealtimeIndicator() {
  const [status, setStatus] = React.useState(
    // Bootstrap from the singleton — if a token is present we're already
    // attempting to connect, so avoid the misleading "Inactif" flash.
    realtime.status === "idle" && typeof window !== "undefined" && localStorage.getItem("st_access_token")
      ? "connecting"
      : realtime.status,
  );
  React.useEffect(() => realtime.onStatus(setStatus), []);
  const config = {
    open:       { label: "Temps réel actif",  color: "text-emerald-400", dot: "bg-emerald-400", Icon: Wifi,    pulse: true },
    connecting: { label: "Connexion…",        color: "text-amber-400",   dot: "bg-amber-400",   Icon: Wifi,    pulse: true },
    closed:     { label: "Hors ligne",        color: "text-zinc-500",    dot: "bg-zinc-500",    Icon: WifiOff, pulse: false },
    error:      { label: "Erreur connexion",  color: "text-red-400",     dot: "bg-red-400",     Icon: WifiOff, pulse: false },
    idle:       { label: "Inactif",           color: "text-zinc-500",    dot: "bg-zinc-500",    Icon: WifiOff, pulse: false },
  }[status] || { label: status, color: "text-zinc-500", dot: "bg-zinc-500", Icon: WifiOff, pulse: false };
  const Icon = config.Icon;
  return (
    <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.25em]" data-testid="realtime-status" title={config.label}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot} ${config.pulse ? "animate-pulse" : ""}`} />
      <Icon size={10} className={config.color} strokeWidth={2} />
      <span className={config.color}>{config.label}</span>
    </div>
  );
}

const navSections = [
  {
    title: "Tableau de bord",
    items: [
      { to: "/admin", icon: LayoutDashboard, label: "Vue générale", end: true, id: "nav-admin-dashboard" },
    ],
  },
  {
    title: "Planification",
    items: [
      { to: "/admin/stops", icon: MapPin, label: "Arrêts", id: "nav-admin-stops" },
      { to: "/admin/inter-stops", icon: Route, label: "Inter-arrêts", id: "nav-admin-inter-stops" },
    ],
  },
  {
    title: "Exploitation",
    items: [
      { to: "/admin/live-tracking", icon: Radio, label: "Centre d'exploitation", id: "nav-admin-live-tracking" },
      { to: "/admin/dispatch", icon: Sparkles, label: "Planning Bus", id: "nav-admin-dispatch" },
      { to: "/admin/fleet-assignments", icon: LinkIcon, label: "Affectations flotte", id: "nav-admin-fleet-assignments" },
      { to: "/admin/substitutions", icon: RefreshCw, label: "Exceptions", id: "nav-admin-substitutions" },
      { to: "/admin/exploitation", icon: Activity, label: "Exploitation services", id: "nav-admin-exploitation" },
      // GTFS (legacy) — masqué du menu sur demande utilisateur (2026-06-16).
      // Toujours accessible par URL /admin/gtfs-trips pour le backlog technique.
      // { to: "/admin/gtfs-trips", icon: Map, label: "Tournées", id: "nav-admin-tournees" },
      { to: "/admin/missions", icon: ClipboardList, label: "Missions", id: "nav-admin-missions" },
      { to: "/admin/incidents", icon: ShieldAlert, label: "Retards & incidents", id: "nav-admin-incidents" },
    ],
  },
  {
    title: "Flotte",
    items: [
      { to: "/admin/vehicles", icon: Car, label: "Bus", id: "nav-admin-vehicles" },
      // Legacy "Affectations" (collection GTFS routes ↔ vehicles) — masqué sur
      // demande utilisateur (2026-06-16). Utilisez "Affectations flotte" (basé
      // sur tour_assignments) côté Exploitation à la place. Route conservée.
      // { to: "/admin/affectations", icon: LinkIcon, label: "Affectations", id: "nav-admin-affectations" },
      { to: "/admin/programs", icon: FileSpreadsheet, label: "Import tournées", id: "nav-admin-programs" },
      { to: "/admin/calendar", icon: CalendarDays, label: "Calendrier scolaire", id: "nav-admin-calendar" },
      { to: "/admin/drivers", icon: UserCog, label: "Chauffeurs", id: "nav-admin-drivers" },
      { to: "/admin/children", icon: Users, label: "Enfants / Passagers", id: "nav-admin-children" },
      { to: "/admin/stops-map", icon: Map, label: "POI", labelByTenant: { MBC: "POI MBC" }, id: "nav-admin-stops-map" },
      { to: "/admin/bluetooth-tags", icon: Bluetooth, label: "Tags Bluetooth", id: "nav-admin-bluetooth-tags" },
    ],
  },
  {
    title: "Analytics",
    items: [
      { to: "/admin/dashboards/bus", icon: Car, label: "Dashboard Bus", id: "nav-admin-dashboard-bus" },
      { to: "/admin/dashboards/services", icon: Activity, label: "Dashboard Services", id: "nav-admin-dashboard-services" },
      { to: "/admin/dashboards/drivers", icon: UserCog, label: "Dashboard Chauffeurs", id: "nav-admin-dashboard-drivers" },
      { to: "/admin/analytics", icon: BarChart3, label: "Rapports", id: "nav-admin-analytics" },
      { to: "/admin/driver-performance", icon: Award, label: "Score chauffeurs", id: "nav-admin-driver-perf" },
      { to: "/admin/ai-recommendations", icon: Sparkles, label: "Optimisation IA", id: "nav-admin-ai-rec" },
    ],
  },
  {
    title: "Admin",
    items: [
      { to: "/admin/users", icon: Users, label: "Utilisateurs", id: "nav-admin-users" },
      { to: "/admin/settings", icon: SettingsIcon, label: "Paramètres", id: "nav-admin-settings" },
      { to: "/admin/navixy-sync", icon: RefreshCw, label: "Sync Logitrak", id: "nav-admin-navixy-sync" },
    ],
  },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-white">
      <Toaster position="top-right" richColors closeButton />

      <aside className="w-60 shrink-0 bg-zinc-900 text-white flex flex-col border-r border-zinc-900" data-testid="admin-sidebar">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2.5">
          <div className="w-9 h-9 bg-[#FFC60A] border border-zinc-900 flex items-center justify-center shadow-[3px_3px_0_0_#0A0A0B]">
            <Bus size={17} className="text-zinc-900" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-lg tracking-tighter leading-none">LOGIBUS</div>
            <div className="text-[9px] font-mono tracking-[0.3em] uppercase text-zinc-400 truncate mt-1" data-testid="admin-tenant-name">
              {user?.tenant?.name || "Admin Console"}
            </div>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            title="Déconnexion"
            className="shrink-0 p-2 bg-[#FFC60A] text-zinc-900 border border-zinc-900 hover:bg-[#ffd43a] transition-colors"
          >
            <LogOut size={15} strokeWidth={2.5} />
          </button>
        </div>
        <nav className="px-2.5 py-3 flex-1 overflow-y-auto">
          {navSections.map((section, sIdx) => (
            <div key={section.title} className={sIdx > 0 ? "mt-4 pt-3 border-t border-zinc-800/60" : ""}>
              <div className="px-2 mb-1.5 text-[9px] font-mono tracking-[0.3em] uppercase text-zinc-500">{section.title}</div>
              <div className="space-y-0.5">
                {section.items.map(({ to, icon: Icon, label: baseLabel, labelByTenant, end, id }) => {
                  const label = labelByTenant?.[user?.tenant?.code] || baseLabel;
                  return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    data-testid={id}
                    className={({ isActive }) =>
                      `group flex items-center gap-2.5 px-2.5 py-1.5 text-[13px] font-medium border-l-2 transition-all ${
                        isActive
                          ? "bg-white text-zinc-900 border-[#FFC60A] shadow-[inset_0_-1px_0_0_rgba(0,0,0,0.06)]"
                          : "border-transparent text-zinc-300 hover:bg-zinc-800/70 hover:text-white hover:border-zinc-700"
                      }`
                    }
                  >
                    <Icon size={15} strokeWidth={2} /> <span className="truncate">{label}</span>
                  </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <RealtimeIndicator />
          <div className="text-[9px] font-mono tracking-[0.3em] uppercase text-zinc-500 mt-3">École · Code</div>
          <button
            data-testid="admin-tenant-code"
            onClick={() => { navigator.clipboard?.writeText(user?.tenant?.code || ""); }}
            title="Copier le code"
            className="mt-1.5 inline-flex bg-[#FFC60A] text-zinc-900 px-2 py-0.5 font-mono text-xs font-bold tracking-widest uppercase border border-zinc-900 hover:bg-[#ffd43a] transition-colors"
          >
            {user?.tenant?.code || "—"}
          </button>
          <div className="mt-3 text-[9px] font-mono tracking-[0.3em] uppercase text-zinc-500">Connecté</div>
          <div className="text-sm font-semibold truncate mt-0.5">{user?.name}</div>
          <div className="text-[11px] text-zinc-400 truncate font-mono">{user?.email}</div>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden bg-zinc-50/50">
        <Outlet />
        <HelpButton />
      </main>
    </div>
  );
}
